/**
 * Admin Homepage with Embedded Revenue Governance
 * 
 * Revenue Governance dashboards are displayed inline within this page,
 * not as separate pages. Each section shows key metrics directly.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { 
  Shield, DollarSign, CreditCard, TrendingUp, TrendingDown,
  Users, BookOpen, Settings, BarChart3, RefreshCw, AlertTriangle,
  Loader2, Eye, ChevronDown, ChevronUp, Download, Trash2, Search, CheckCircle2,
  Building2, PenTool, UserCheck
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import AdminNav from "../components/AdminNav";
import AdminTopMenuBar from "../components/AdminTopMenuBar";
import AdminMobileBottomNav from "../components/AdminMobileBottomNav";
import UniversalFooter from "../components/UniversalFooter";
import DateFilter from "../components/admin/control-panel/DateFilter";
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

// API_URL imported from centralized config

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatCurrency = (cents) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(cents / 100);
};

const formatCredits = (credits) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(credits);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// ============================================================================
// REVENUE DASHBOARD SECTION
// ============================================================================

const RevenueDashboardSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    return { start: todayStr, end: todayStr };
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(
        `${API_URL}/api/nfm/admin/governance/revenue/overview?period_type=custom&start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { headers }
      );
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error fetching revenue:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Card className="bg-emerald-900/20 border-emerald-700/50" data-testid="revenue-dashboard-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-800/50">
              <DollarSign className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <CardTitle className="text-white font-bold">Revenue Dashboard</CardTitle>
              <CardDescription className="text-emerald-300/70">
                Revenue overview for selected period
              </CardDescription>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setExpanded(!expanded)}
            className="text-white hover:bg-emerald-900/30"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Date Filter Controls */}
        <DateFilter 
          dateRange={dateRange} 
          onDateRangeChange={setDateRange} 
          onRefresh={fetchData} 
          loading={loading} 
        />

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
          </div>
        ) : (
          <>
            {/* Summary Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Gross Revenue</div>
                <div className="text-2xl font-bold text-emerald-400 mt-1">
                  {formatCurrency(data?.gross_revenue_cents || 0)}
                </div>
                <div className="text-xs text-slate-500 mt-1">{dateRange.start === dateRange.end ? dateRange.start : `${dateRange.start} to ${dateRange.end}`}</div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Bundles</div>
                <div className="text-2xl font-bold text-white mt-1">
                  {formatCurrency(data?.bundle_revenue_cents || 0)}
                </div>
                <div className="text-xs text-slate-500 mt-1">{data?.bundle_transaction_count || 0} transactions</div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Subscriptions</div>
                <div className="text-2xl font-bold text-white mt-1">
                  {formatCurrency(data?.subscription_revenue_cents || 0)}
                </div>
                <div className="text-xs text-slate-500 mt-1">{data?.subscription_transaction_count || 0} transactions</div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Active Readers</div>
                <div className="text-2xl font-bold text-white mt-1">
                  {data?.active_paying_readers || 0}
                </div>
                <div className="text-xs text-slate-500 mt-1">{data?.new_paying_readers || 0} new</div>
              </div>
            </div>

            {/* Expanded: Daily Breakdown */}
            {expanded && data?.daily_breakdown?.length > 0 && (
              <div className="mt-6 rounded-lg border border-slate-700 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700 bg-slate-800/50">
                      <TableHead className="text-white font-bold">Date</TableHead>
                      <TableHead className="text-white font-bold text-right">Bundles</TableHead>
                      <TableHead className="text-white font-bold text-right">Subscriptions</TableHead>
                      <TableHead className="text-white font-bold text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.daily_breakdown.slice(0, 31).map((day) => (
                      <TableRow key={day.date} className="border-slate-700">
                        <TableCell className="text-white font-medium">{day.date}</TableCell>
                        <TableCell className="text-right text-slate-300">
                          {formatCurrency(day.bundle_revenue_cents)} ({day.bundle_count})
                        </TableCell>
                        <TableCell className="text-right text-slate-300">
                          {formatCurrency(day.subscription_revenue_cents)} ({day.subscription_count})
                        </TableCell>
                        <TableCell className="text-right font-bold text-emerald-400">
                          {formatCurrency(day.total_revenue_cents)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {expanded && (!data?.daily_breakdown || data.daily_breakdown.length === 0) && (
              <div className="mt-6 text-center text-slate-500 py-4">
                No transactions found for selected period
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// CREDIT LIABILITY SECTION
// ============================================================================

const CreditLiabilitySection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    return { start: todayStr, end: todayStr };
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(
        `${API_URL}/api/nfm/admin/governance/credits/liability?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { headers }
      );
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error fetching liability:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const liabilityRatio = data?.total_credits_issued > 0
    ? ((data?.outstanding_credits / data?.total_credits_issued) * 100).toFixed(1)
    : 0;

  return (
    <Card className="bg-amber-900/20 border-amber-700/50" data-testid="credit-liability-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-800/50">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-white font-bold">Credit Liability</CardTitle>
              <CardDescription className="text-amber-300/70">
                Outstanding credits & consumption velocity
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setExpanded(!expanded)}
              className="text-white hover:bg-amber-900/30"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Date Filter Controls */}
        <DateFilter 
          dateRange={dateRange} 
          onDateRangeChange={setDateRange} 
          onRefresh={fetchData} 
          loading={loading} 
        />

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
          </div>
        ) : (
          <>
            {/* Summary Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Outstanding</div>
                <div className="text-2xl font-bold text-amber-400 mt-1">
                  {formatCredits(data?.outstanding_credits || 0)} RC
                </div>
                <div className="text-xs text-slate-500 mt-1">{liabilityRatio}% of issued</div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Total Issued</div>
                <div className="text-2xl font-bold text-white mt-1">
                  {formatCredits(data?.total_credits_issued || 0)} RC
                </div>
                <div className="text-xs text-slate-500 mt-1">All-time allocation</div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Total Consumed</div>
                <div className="text-2xl font-bold text-white mt-1">
                  {formatCredits(data?.total_credits_consumed || 0)} RC
                </div>
                <div className="text-xs text-slate-500 mt-1">All-time usage</div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Velocity</div>
                <div className="text-2xl font-bold text-white mt-1">
                  {formatCredits(data?.consumption_velocity_daily || 0)}/day
                </div>
                <div className="text-xs text-slate-500 mt-1">{formatCredits(data?.consumption_velocity_weekly || 0)}/week</div>
              </div>
            </div>

            {/* Expanded: Breakdown */}
            {expanded && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-white font-bold mb-3">Credit Sources</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-400">From Bundles</span>
                      <span className="text-white font-medium">{formatCredits(data?.credits_from_bundles || 0)} RC</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">From Subscriptions</span>
                      <span className="text-white font-medium">{formatCredits(data?.credits_from_subscriptions || 0)} RC</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">From Promotions</span>
                      <span className="text-white font-medium">{formatCredits(data?.credits_from_promo || 0)} RC</span>
                    </div>
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-white font-bold mb-3">Reader Distribution</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Readers with Balance</span>
                      <span className="text-white font-medium">{data?.readers_with_balance || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Average Balance</span>
                      <span className="text-white font-medium">{formatCredits(data?.average_balance_per_reader || 0)} RC</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// READER CONSUMPTION SECTION
// ============================================================================

const ReaderConsumptionSection = () => {
  const [readers, setReaders] = useState([]);
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [viewMode, setViewMode] = useState('readers');
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    return { start: todayStr, end: todayStr };
  });
  
  // Calculate totals from actual consumed data
  const [totalCreditsConsumed, setTotalCreditsConsumed] = useState(0);
  const [totalTitlesConsumed, setTotalTitlesConsumed] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const dateParams = `start_date=${dateRange.start}&end_date=${dateRange.end}`;
      const [readersRes, titlesRes] = await Promise.all([
        fetch(`${API_URL}/api/nfm/admin/governance/consumption/readers?limit=10&${dateParams}`, { headers }),
        fetch(`${API_URL}/api/nfm/admin/governance/consumption/titles?limit=10&${dateParams}`, { headers })
      ]);

      if (readersRes.ok) {
        const data = await readersRes.json();
        setReaders(data.readers || []);
        // Calculate total from all readers' consumed credits
        const totalConsumed = (data.readers || []).reduce((acc, r) => acc + (r.total_credits_consumed || 0), 0);
        setTotalCreditsConsumed(totalConsumed);
      }
      if (titlesRes.ok) {
        const data = await titlesRes.json();
        setTitles(data.titles || []);
        setTotalTitlesConsumed((data.titles || []).length);
      }
    } catch (error) {
      console.error('Error fetching consumption:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDrilldown = async (userId) => {
    setDrilldownLoading(true);
    setDrilldownOpen(true);
    try {
      const token = localStorage.getItem('accessToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API_URL}/api/nfm/admin/governance/drilldown/reader/${userId}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setDrilldownData(data);
      } else {
        toast.error('Failed to load reader details');
      }
    } catch (error) {
      console.error('Error fetching drilldown:', error);
      toast.error('Error loading reader details');
    } finally {
      setDrilldownLoading(false);
    }
  };

  return (
    <>
      <Card className="bg-purple-900/20 border-purple-700/50" data-testid="reader-consumption-section">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-800/50">
                <BarChart3 className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-white font-bold">Reader Consumption</CardTitle>
                <CardDescription className="text-purple-300/70">
                  Credits actually consumed by readers
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setExpanded(!expanded)}
                className="text-white hover:bg-purple-900/30"
              >
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Date Filter Controls */}
          <DateFilter 
            dateRange={dateRange} 
            onDateRangeChange={setDateRange} 
            onRefresh={fetchData} 
            loading={loading} 
          />

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
            </div>
          ) : (
            <>
              {/* Summary Metrics - Based on CONSUMED credits */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Credits Consumed</div>
                  <div className="text-2xl font-bold text-purple-400 mt-1">
                    {formatCredits(totalCreditsConsumed)} RC
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Top 10 readers</div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Active Readers</div>
                  <div className="text-2xl font-bold text-white mt-1">
                    {readers.length}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">With consumption</div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Titles Consumed</div>
                  <div className="text-2xl font-bold text-white mt-1">
                    {totalTitlesConsumed}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Unique titles</div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Top Consumer</div>
                  <div className="text-lg font-bold text-white mt-1 truncate">
                    {readers[0]?.name || '—'}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {readers[0] ? `${formatCredits(readers[0].total_credits_consumed)} RC` : 'No data'}
                  </div>
                </div>
              </div>

              {/* Expanded: Tables */}
              {expanded && (
                <div className="mt-6 space-y-4">
                  {/* View Toggle */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => setViewMode('readers')}
                      className={viewMode === 'readers' 
                        ? 'bg-purple-600 hover:bg-purple-700 text-white font-bold' 
                        : 'bg-slate-700 hover:bg-slate-600 text-white'}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      By Reader
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setViewMode('titles')}
                      className={viewMode === 'titles' 
                        ? 'bg-purple-600 hover:bg-purple-700 text-white font-bold' 
                        : 'bg-slate-700 hover:bg-slate-600 text-white'}
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      By Title
                    </Button>
                  </div>

                  {/* Readers Table */}
                  {viewMode === 'readers' && readers.length > 0 && (
                    <div className="rounded-lg border border-slate-700 overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-slate-700 bg-slate-800/50">
                            <TableHead className="text-white font-bold">Reader</TableHead>
                            <TableHead className="text-white font-bold">Membership</TableHead>
                            <TableHead className="text-white font-bold text-right">Consumed</TableHead>
                            <TableHead className="text-white font-bold text-right">Balance</TableHead>
                            <TableHead className="text-white font-bold"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {readers.map((reader) => (
                            <TableRow 
                              key={reader.user_id} 
                              className="border-slate-700 cursor-pointer hover:bg-slate-700/50"
                              onClick={() => openDrilldown(reader.user_id)}
                            >
                              <TableCell>
                                <div>
                                  <div className="text-white font-medium">{reader.name}</div>
                                  <div className="text-xs text-slate-500">{reader.email}</div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge className={
                                  reader.membership_type === 'Subscriber'
                                    ? 'bg-purple-500/20 text-purple-300 border-purple-500/30'
                                    : reader.membership_type === 'ALaCarte'
                                    ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                                    : 'bg-slate-500/20 text-slate-300 border-slate-500/30'
                                }>
                                  {reader.membership_type === 'ALaCarte' ? 'A La Carte' : reader.membership_type}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right font-medium text-purple-400">
                                {formatCredits(reader.total_credits_consumed)} RC
                              </TableCell>
                              <TableCell className="text-right">
                                <span className={reader.current_balance < 0 ? 'text-red-400' : 'text-emerald-400'}>
                                  {formatCredits(reader.current_balance)} RC
                                </span>
                              </TableCell>
                              <TableCell>
                                <Eye className="w-4 h-4 text-slate-400" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {/* Titles Table */}
                  {viewMode === 'titles' && titles.length > 0 && (
                    <div className="rounded-lg border border-slate-700 overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-slate-700 bg-slate-800/50">
                            <TableHead className="text-white font-bold">Title</TableHead>
                            <TableHead className="text-white font-bold">Author</TableHead>
                            <TableHead className="text-white font-bold text-right">Consumed</TableHead>
                            <TableHead className="text-white font-bold text-right">Readers</TableHead>
                            <TableHead className="text-white font-bold text-right">Sessions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {titles.map((title) => (
                            <TableRow key={title.title_id} className="border-slate-700">
                              <TableCell className="text-white font-medium">{title.title}</TableCell>
                              <TableCell className="text-slate-400">{title.author}</TableCell>
                              <TableCell className="text-right font-medium text-purple-400">
                                {formatCredits(title.total_credits_consumed)} RC
                              </TableCell>
                              <TableCell className="text-right text-slate-300">{title.unique_readers}</TableCell>
                              <TableCell className="text-right text-slate-300">{title.total_sessions}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Reader Drilldown Dialog */}
      <Dialog open={drilldownOpen} onOpenChange={setDrilldownOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-bold">Reader Details</DialogTitle>
            <DialogDescription className="text-slate-400">
              Complete financial activity for this reader
            </DialogDescription>
          </DialogHeader>

          {drilldownLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : drilldownData ? (
            <div className="space-y-6">
              {/* Reader Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-slate-400">Name</div>
                  <div className="text-white font-bold">{drilldownData.name}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-400">Email</div>
                  <div className="text-white font-bold">{drilldownData.email}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-400">Membership</div>
                  <Badge className="mt-1">
                    {drilldownData.membership_type === 'ALaCarte' ? 'A La Carte' : drilldownData.membership_type}
                  </Badge>
                </div>
                <div>
                  <div className="text-sm text-slate-400">Current Balance</div>
                  <div className={`font-bold ${drilldownData.current_balance < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {formatCredits(drilldownData.current_balance)} RC
                  </div>
                </div>
              </div>

              {/* Credit Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <div className="text-xs text-slate-400 font-semibold uppercase">Issued</div>
                  <div className="text-lg font-bold text-white mt-1">{formatCredits(drilldownData.total_credits_issued)}</div>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <div className="text-xs text-slate-400 font-semibold uppercase">Consumed</div>
                  <div className="text-lg font-bold text-purple-400 mt-1">{formatCredits(drilldownData.total_credits_consumed)}</div>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <div className="text-xs text-slate-400 font-semibold uppercase">Transactions</div>
                  <div className="text-lg font-bold text-white mt-1">{drilldownData.transaction_count}</div>
                </div>
              </div>

              {/* Ledger Entries */}
              {drilldownData.ledger_entries?.length > 0 && (
                <div>
                  <h4 className="text-white font-bold mb-2">Credit Ledger (Recent)</h4>
                  <div className="max-h-48 overflow-y-auto rounded border border-slate-700">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700 bg-slate-700/30">
                          <TableHead className="text-white font-bold text-xs">Type</TableHead>
                          <TableHead className="text-white font-bold text-xs">Amount</TableHead>
                          <TableHead className="text-white font-bold text-xs">Source</TableHead>
                          <TableHead className="text-white font-bold text-xs">Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {drilldownData.ledger_entries.slice(0, 15).map((entry, i) => (
                          <TableRow key={i} className="border-slate-700">
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={`text-xs ${entry.type === 'credit' ? 'text-emerald-400 border-emerald-400/30' : 'text-red-400 border-red-400/30'}`}
                              >
                                {entry.type}
                              </Badge>
                            </TableCell>
                            <TableCell className={`text-xs font-medium ${entry.type === 'credit' ? 'text-emerald-400' : 'text-red-400'}`}>
                              {entry.type === 'credit' ? '+' : ''}{formatCredits(entry.amount)}
                            </TableCell>
                            <TableCell className="text-slate-400 text-xs">{entry.source || '—'}</TableCell>
                            <TableCell className="text-slate-400 text-xs">{formatDate(entry.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};

// ============================================================================
// MANUSCRIPT CLEANUP SECTION
// ============================================================================

const ManuscriptCleanupSection = () => {
  const [scanning, setScanning] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [cleaningR2, setCleaningR2] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  const [scanResults, setScanResults] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showR2ConfirmDialog, setShowR2ConfirmDialog] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleReconcile = async () => {
    setReconciling(true);
    setReconcileResult(null);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/admin/manuscripts/reconcile`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Reconciliation failed');
      }
      
      const data = await response.json();
      setReconcileResult(data);
      
      if (data.manuscripts_reconciled > 0) {
        toast.success(`Reconciled ${data.manuscripts_reconciled} stuck manuscripts!`);
      } else {
        toast.info('No stuck manuscripts found to reconcile');
      }
    } catch (error) {
      console.error('Reconcile error:', error);
      toast.error(`Reconciliation failed: ${error.message}`);
    } finally {
      setReconciling(false);
    }
  };

  const handleScan = async () => {
    setScanning(true);
    setScanResults(null);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/admin/manuscripts/cleanup/scan`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Scan failed');
      }
      
      const data = await response.json();
      setScanResults(data);
      setExpanded(true);
      
      if (data.total_issues === 0) {
        toast.success('Database is clean - no orphaned records found');
      } else {
        toast.warning(`Found ${data.total_issues} issues that need attention`);
      }
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Failed to scan for orphaned records');
    } finally {
      setScanning(false);
    }
  };

  const handleExecuteCleanup = async () => {
    setExecuting(true);
    setShowConfirmDialog(false);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/admin/manuscripts/cleanup/execute`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Cleanup failed');
      }
      
      const data = await response.json();
      toast.success(`Cleanup complete: ${data.total_deleted} records removed`);
      
      // Re-scan to show updated state
      handleScan();
    } catch (error) {
      console.error('Cleanup error:', error);
      toast.error('Failed to execute cleanup');
    } finally {
      setExecuting(false);
    }
  };

  const handleR2Cleanup = async () => {
    setCleaningR2(true);
    setShowR2ConfirmDialog(false);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/admin/manuscripts/cleanup/r2-orphans`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'R2 cleanup failed');
      }
      
      const data = await response.json();
      toast.success(`R2 Cleanup complete: ${data.folders_deleted} folders, ${data.files_deleted} files removed`);
      
      // Re-scan to show updated state
      handleScan();
    } catch (error) {
      console.error('R2 Cleanup error:', error);
      toast.error(`Failed to clean R2: ${error.message}`);
    } finally {
      setCleaningR2(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'HIGH': return 'text-red-400 border-red-400/30 bg-red-900/20';
      case 'MEDIUM': return 'text-amber-400 border-amber-400/30 bg-amber-900/20';
      case 'LOW': return 'text-blue-400 border-blue-400/30 bg-blue-900/20';
      default: return 'text-slate-400 border-slate-400/30 bg-slate-900/20';
    }
  };

  return (
    <Card className="bg-rose-900/20 border-rose-700/50" data-testid="manuscript-cleanup-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-rose-800/50">
              <Trash2 className="w-5 h-5 text-rose-400" />
            </div>
            <div>
              <CardTitle className="text-white font-bold">Manuscript Cleanup</CardTitle>
              <CardDescription className="text-rose-300/70">
                Scan and remove orphaned database records
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Reconcile Stuck Manuscripts Button */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleReconcile}
              disabled={reconciling || scanning}
              className="text-green-400 border-green-400/30 hover:bg-green-900/30"
              data-testid="reconcile-btn"
              title="Fix manuscripts stuck in 'processing' state"
            >
              {reconciling ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {reconciling ? 'Reconciling...' : 'Reconcile Stuck'}
            </Button>
            
            {/* Scan for Orphans Button */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleScan}
              disabled={scanning || reconciling}
              className="text-rose-400 border-rose-400/30 hover:bg-rose-900/30"
              data-testid="scan-orphans-btn"
            >
              {scanning ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              {scanning ? 'Scanning...' : 'Scan for Orphans'}
            </Button>
            {(scanResults || reconcileResult) && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setExpanded(!expanded)}
                className="text-white hover:bg-rose-900/30"
              >
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      {/* Reconcile Result Display */}
      {reconcileResult && expanded && (
        <CardContent className="space-y-4 border-b border-slate-700 pb-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-green-400" />
            <h4 className="text-sm font-bold text-white">Reconciliation Results</h4>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-slate-800/50 text-center">
              <p className="text-2xl font-bold text-white">{reconcileResult.manuscripts_checked}</p>
              <p className="text-xs text-slate-400">Checked</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50 text-center">
              <p className={`text-2xl font-bold ${reconcileResult.manuscripts_reconciled > 0 ? 'text-green-400' : 'text-slate-400'}`}>
                {reconcileResult.manuscripts_reconciled}
              </p>
              <p className="text-xs text-slate-400">Reconciled</p>
            </div>
          </div>
          {reconcileResult.reconciled_details?.length > 0 && (
            <div className="text-xs text-slate-400 space-y-1">
              <p className="font-medium text-green-400">Reconciled manuscripts:</p>
              {reconcileResult.reconciled_details.map((item, i) => (
                <div key={i} className="pl-2 border-l border-green-500/30">
                  {item.title} - {item.audio_files_found} audio files found
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
      
      {scanResults && expanded && (
        <CardContent className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="p-3 rounded-lg bg-slate-800/50 text-center">
              <p className="text-2xl font-bold text-white">{scanResults.valid_manuscripts_count}</p>
              <p className="text-xs text-slate-400">Valid Manuscripts</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50 text-center">
              <p className={`text-2xl font-bold ${scanResults.summary.invalid_title_refs > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {scanResults.summary.invalid_title_refs}
              </p>
              <p className="text-xs text-slate-400">Invalid Title Refs</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50 text-center">
              <p className={`text-2xl font-bold ${scanResults.summary.orphaned_reader_library > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                {scanResults.summary.orphaned_reader_library}
              </p>
              <p className="text-xs text-slate-400">Orphaned Reader</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50 text-center">
              <p className={`text-2xl font-bold ${scanResults.summary.duplicate_titles > 0 ? 'text-blue-400' : 'text-green-400'}`}>
                {scanResults.summary.duplicate_titles}
              </p>
              <p className="text-xs text-slate-400">Duplicate Titles</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50 text-center">
              <p className={`text-2xl font-bold ${scanResults.summary.r2_orphans > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                {scanResults.summary.r2_orphans}
              </p>
              <p className="text-xs text-slate-400">R2 Orphans</p>
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {scanResults.status === 'clean' ? (
                <Badge className="bg-green-600 text-white">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Database Clean
                </Badge>
              ) : (
                <Badge className="bg-amber-600 text-white">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {scanResults.total_issues} Issues Found
                </Badge>
              )}
              <span className="text-xs text-slate-500">
                Scanned: {new Date(scanResults.scan_timestamp).toLocaleString()}
              </span>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2">
              {/* DB Cleanup - only show if there are DB issues */}
              {(scanResults.summary?.invalid_title_refs > 0 || scanResults.summary?.orphaned_reader_library > 0) && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowConfirmDialog(true)}
                  disabled={executing || cleaningR2}
                  data-testid="execute-cleanup-btn"
                >
                  {executing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  {executing ? 'Cleaning DB...' : 'Clean DB Records'}
                </Button>
              )}
              
              {/* R2 Cleanup - only show if there are R2 orphans */}
              {scanResults.summary?.r2_orphans > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowR2ConfirmDialog(true)}
                  disabled={executing || cleaningR2}
                  data-testid="execute-r2-cleanup-btn"
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {cleaningR2 ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  {cleaningR2 ? 'Cleaning R2...' : `Clean R2 (${scanResults.summary.r2_orphans})`}
                </Button>
              )}
            </div>
          </div>

          {/* Issue Details */}
          {scanResults.issues?.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-bold text-white">Issue Details</h4>
              {scanResults.issues.map((issue, i) => (
                <div key={i} className={`p-3 rounded-lg border ${getSeverityColor(issue.severity)}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm">{issue.description}</span>
                    <Badge variant="outline" className={getSeverityColor(issue.severity)}>
                      {issue.severity} • {issue.count}
                    </Badge>
                  </div>
                  {issue.items && (
                    <div className="text-xs text-slate-400 mt-2 max-h-24 overflow-y-auto">
                      {issue.items.slice(0, 5).map((item, j) => (
                        <div key={j} className="py-1 border-b border-slate-700/50 last:border-0">
                          {item.title_name || item.title || JSON.stringify(item)}
                        </div>
                      ))}
                      {issue.items.length > 5 && (
                        <div className="py-1 text-slate-500">...and {issue.items.length - 5} more</div>
                      )}
                    </div>
                  )}
                  {issue.orphaned_ids && (
                    <div className="text-xs text-slate-400 mt-2">
                      IDs: {issue.orphaned_ids.slice(0, 3).map(id => id.slice(0, 8)).join(', ')}
                      {issue.orphaned_ids.length > 3 && ` ...+${issue.orphaned_ids.length - 3} more`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}

      {/* DB Cleanup Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Confirm Database Cleanup
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will permanently delete orphaned records from the database:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>{scanResults?.summary?.invalid_title_refs || 0} invalid publisher title references</li>
                <li>{scanResults?.summary?.orphaned_reader_library || 0} orphaned reader library entries</li>
              </ul>
              <p className="mt-3 text-amber-400 font-medium">This action cannot be undone.</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleExecuteCleanup}>
              <Trash2 className="w-4 h-4 mr-2" />
              Clean DB Records
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* R2 Cleanup Confirmation Dialog */}
      <Dialog open={showR2ConfirmDialog} onOpenChange={setShowR2ConfirmDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Confirm R2 Storage Cleanup
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              <p className="text-red-400 font-medium mb-2">⚠️ DESTRUCTIVE OPERATION</p>
              This will permanently delete {scanResults?.summary?.r2_orphans || 0} orphaned manuscript folders from R2 storage, including:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>All audio files (MP3s)</li>
                <li>NFD files (book structure data)</li>
                <li>Any other manuscript assets</li>
              </ul>
              <p className="mt-3 text-red-400 font-bold">This action CANNOT be undone. Files will be permanently deleted.</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowR2ConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleR2Cleanup}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete R2 Files Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ============================================================================
// PLATFORM ANALYTICS SECTION
// ============================================================================

const PlatformAnalyticsSection = () => {
  const [data, setData] = useState(null);
  const [rawData, setRawData] = useState({ books: [], authors: [], publishers: [], managedAuthors: [] });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Fetch all data in parallel
      const [booksResponse, authorsResponse, publishersResponse, managedAuthorsResponse] = await Promise.all([
        fetch(`${API_URL}/api/admin/manuscripts`, { headers }),
        fetch(`${API_URL}/api/admin/authors`, { headers }),
        fetch(`${API_URL}/api/admin/publishers`, { headers }),
        fetch(`${API_URL}/api/admin/housekeeping/all-managed-authors`, { headers })
      ]);
      
      const booksData = booksResponse.ok ? await booksResponse.json() : { manuscripts: [] };
      const authorsData = authorsResponse.ok ? await authorsResponse.json() : { authors: [] };
      const publishersData = publishersResponse.ok ? await publishersResponse.json() : { publishers: [] };
      const managedAuthorsData = managedAuthorsResponse.ok ? await managedAuthorsResponse.json() : { managed_authors: [] };
      
      // Extract arrays from wrapped responses
      const books = booksData.manuscripts || [];
      const authors = authorsData.authors || [];
      const publishers = publishersData.publishers || [];
      const managedAuthors = managedAuthorsData.managed_authors || (Array.isArray(managedAuthorsData) ? managedAuthorsData : []);
      
      setRawData({ books, authors, publishers, managedAuthors });
      
      // Calculate metrics
      const activeBooks = books.filter(b => b.status === 'active' || b.status === 'published').length;
      const inactiveBooks = books.filter(b => b.status !== 'active' && b.status !== 'published').length;
      
      setData({
        activeBooks,
        inactiveBooks,
        totalAuthors: authors.length,
        totalPublishers: publishers.length,
        managedAuthors: managedAuthors.length
      });
    } catch (error) {
      console.error('Error fetching platform analytics:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCardClick = (type) => {
    setModalType(type);
    setModalOpen(true);
  };

  const getModalData = () => {
    const { books, authors, publishers, managedAuthors } = rawData;
    
    switch (modalType) {
      case 'activeBooks':
        return books
          .filter(b => b.status === 'active' || b.status === 'published')
          .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
          .map(b => ({
            title: b.title || 'Untitled',
            author: b.author_name || b.author || 'Unknown Author',
            date: b.activated_at || b.published_at || b.created_at
          }));
      case 'inactiveBooks':
        return books
          .filter(b => b.status !== 'active' && b.status !== 'published')
          .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
          .map(b => ({
            title: b.title || 'Untitled',
            author: b.author_name || b.author || 'Unknown Author',
            status: b.status || 'unknown',
            date: b.created_at
          }));
      case 'authors':
        return authors
          .sort((a, b) => (a.author_name || '').localeCompare(b.author_name || ''))
          .map(a => ({
            name: a.author_name || a.pen_name || 'Unknown',
            penName: a.pen_name || '',
            titles: a.title_count || 0,
            date: a.created_at
          }));
      case 'publishers':
        return publishers
          .sort((a, b) => (a.publisher_name || a.dba_name || '').localeCompare(b.publisher_name || b.dba_name || ''))
          .map(p => ({
            name: p.publisher_name || p.dba_name || 'Unknown',
            managedAuthors: p.total_managed_authors || 0,
            status: p.status || 'unknown',
            date: p.created_at
          }));
      case 'managedAuthors':
        return managedAuthors
          .sort((a, b) => (a.pen_name || a.real_name || '').localeCompare(b.pen_name || b.real_name || ''))
          .map(a => {
            const publisher = publishers.find(p => p.publisher_id === a.publisher_id);
            return {
              name: a.pen_name || a.real_name || 'Unknown',
              publisher: publisher?.publisher_name || publisher?.dba_name || 'Unknown Publisher',
              status: a.status || 'unknown',
              date: a.created_at
            };
          });
      default:
        return [];
    }
  };

  const getModalTitle = () => {
    switch (modalType) {
      case 'activeBooks': return 'Active Books';
      case 'inactiveBooks': return 'Inactive Books';
      case 'authors': return 'All Authors';
      case 'publishers': return 'All Publishers';
      case 'managedAuthors': return 'Managed Authors';
      default: return 'Details';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const StatCard = ({ label, value, icon: Icon, color, onClick }) => (
    <div 
      className={`p-4 rounded-lg border ${color} bg-opacity-20 cursor-pointer transition-all hover:scale-105 hover:shadow-lg`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color.replace('border', 'bg').replace('/50', '/30')}`}>
          <Icon className={`w-5 h-5 ${color.replace('border', 'text').replace('/50', '')}`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-white">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : value?.toLocaleString() || 0}
          </p>
          <p className="text-sm text-slate-400">{label}</p>
        </div>
      </div>
    </div>
  );

  const modalData = getModalData();

  return (
    <>
      <Card className="bg-blue-900/20 border-blue-700/50" data-testid="platform-analytics-section">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-800/50">
                <BarChart3 className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-white font-bold">Platform Analytics</CardTitle>
                <CardDescription className="text-blue-300/70">
                  Content and user overview — click any card for details
                </CardDescription>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchData}
              className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard 
              label="Active Books" 
              value={data?.activeBooks} 
              icon={BookOpen} 
              color="border-emerald-500/50"
              onClick={() => handleCardClick('activeBooks')}
            />
            <StatCard 
              label="Inactive Books" 
              value={data?.inactiveBooks} 
              icon={BookOpen} 
              color="border-slate-500/50"
              onClick={() => handleCardClick('inactiveBooks')}
            />
            <StatCard 
              label="Total Authors" 
              value={data?.totalAuthors} 
              icon={PenTool} 
              color="border-purple-500/50"
              onClick={() => handleCardClick('authors')}
            />
            <StatCard 
              label="Total Publishers" 
              value={data?.totalPublishers} 
              icon={Building2} 
              color="border-amber-500/50"
              onClick={() => handleCardClick('publishers')}
            />
            <StatCard 
              label="Managed Authors" 
              value={data?.managedAuthors} 
              icon={UserCheck} 
              color="border-cyan-500/50"
              onClick={() => handleCardClick('managedAuthors')}
            />
          </div>
        </CardContent>
      </Card>

      {/* Details Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">{getModalTitle()}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {modalData.length} record{modalData.length !== 1 ? 's' : ''} found
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  {modalType === 'activeBooks' && (
                    <>
                      <TableHead className="text-slate-300">Title</TableHead>
                      <TableHead className="text-slate-300">Author</TableHead>
                      <TableHead className="text-slate-300">Activated</TableHead>
                    </>
                  )}
                  {modalType === 'inactiveBooks' && (
                    <>
                      <TableHead className="text-slate-300">Title</TableHead>
                      <TableHead className="text-slate-300">Author</TableHead>
                      <TableHead className="text-slate-300">Status</TableHead>
                      <TableHead className="text-slate-300">Created</TableHead>
                    </>
                  )}
                  {modalType === 'authors' && (
                    <>
                      <TableHead className="text-slate-300">Name</TableHead>
                      <TableHead className="text-slate-300">Pen Name</TableHead>
                      <TableHead className="text-slate-300">Titles</TableHead>
                      <TableHead className="text-slate-300">Joined</TableHead>
                    </>
                  )}
                  {modalType === 'publishers' && (
                    <>
                      <TableHead className="text-slate-300">Name</TableHead>
                      <TableHead className="text-slate-300">Managed Authors</TableHead>
                      <TableHead className="text-slate-300">Status</TableHead>
                      <TableHead className="text-slate-300">Joined</TableHead>
                    </>
                  )}
                  {modalType === 'managedAuthors' && (
                    <>
                      <TableHead className="text-slate-300">Name</TableHead>
                      <TableHead className="text-slate-300">Publisher</TableHead>
                      <TableHead className="text-slate-300">Status</TableHead>
                      <TableHead className="text-slate-300">Joined</TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {modalData.map((item, index) => (
                  <TableRow key={index} className="border-slate-700/50 hover:bg-slate-800/50">
                    {modalType === 'activeBooks' && (
                      <>
                        <TableCell className="text-white font-medium">{item.title}</TableCell>
                        <TableCell className="text-slate-300">{item.author}</TableCell>
                        <TableCell className="text-slate-400">{formatDate(item.date)}</TableCell>
                      </>
                    )}
                    {modalType === 'inactiveBooks' && (
                      <>
                        <TableCell className="text-white font-medium">{item.title}</TableCell>
                        <TableCell className="text-slate-300">{item.author}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-slate-400 border-slate-600">
                            {item.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-400">{formatDate(item.date)}</TableCell>
                      </>
                    )}
                    {modalType === 'authors' && (
                      <>
                        <TableCell className="text-white font-medium">{item.name}</TableCell>
                        <TableCell className="text-slate-300">{item.penName || '—'}</TableCell>
                        <TableCell className="text-slate-300">{item.titles}</TableCell>
                        <TableCell className="text-slate-400">{formatDate(item.date)}</TableCell>
                      </>
                    )}
                    {modalType === 'publishers' && (
                      <>
                        <TableCell className="text-white font-medium">{item.name}</TableCell>
                        <TableCell className="text-slate-300">{item.managedAuthors}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`${item.status === 'active' ? 'text-emerald-400 border-emerald-600' : 'text-slate-400 border-slate-600'}`}>
                            {item.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-400">{formatDate(item.date)}</TableCell>
                      </>
                    )}
                    {modalType === 'managedAuthors' && (
                      <>
                        <TableCell className="text-white font-medium">{item.name}</TableCell>
                        <TableCell className="text-slate-300">{item.publisher}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`${item.status === 'active' ? 'text-emerald-400 border-emerald-600' : 'text-slate-400 border-slate-600'}`}>
                            {item.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-400">{formatDate(item.date)}</TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
                {modalData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ============================================================================
// MAIN ADMIN HOME PAGE
// ============================================================================

const AdminHomePage = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <AdminNav />
      <AdminTopMenuBar />
      
      <div className="pb-20 md:pb-0">
        {/* Welcome Header */}
        <header className="bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-b border-slate-800">
          <div className="mx-auto max-w-7xl px-4 py-10 sm:py-12">
            <div className="flex items-center gap-3 mb-3">
              <Shield className="w-8 h-8 text-violet-400" />
              <h1 className="text-3xl sm:text-4xl font-bold text-white">
                Welcome, {user?.name || user?.email || 'Admin'}
              </h1>
            </div>
            <p className="text-sm sm:text-base text-slate-300 max-w-2xl">
              Platform administration and governance console. Monitor revenue, credit liability, and reader consumption below.
            </p>
          </div>
        </header>

        {/* Main Content */}
        <main className="mx-auto max-w-7xl px-4 py-8 space-y-8">
          
          {/* Platform Analytics Section */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-bold text-white">Platform Analytics</h2>
            </div>
            
            <div className="grid grid-cols-1 gap-6">
              <PlatformAnalyticsSection />
            </div>
          </section>

          {/* Revenue Governance Section - EMBEDDED */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-bold text-white">Revenue Governance</h2>
              <Badge variant="outline" className="ml-2 text-xs text-amber-400 border-amber-400/30">
                Read-Only
              </Badge>
            </div>
            
            <div className="grid grid-cols-1 gap-6">
              <RevenueDashboardSection />
              <CreditLiabilitySection />
              <ReaderConsumptionSection />
            </div>
          </section>

          {/* System Maintenance Section */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Trash2 className="w-5 h-5 text-rose-400" />
              <h2 className="text-lg font-bold text-white">System Maintenance</h2>
              <Badge variant="outline" className="ml-2 text-xs text-rose-400 border-rose-400/30">
                Admin Only
              </Badge>
            </div>
            
            <div className="grid grid-cols-1 gap-6">
              <ManuscriptCleanupSection />
            </div>
          </section>
        </main>
      </div>
      
      <AdminMobileBottomNav />
      <UniversalFooter />
    </div>
  );
};

export default AdminHomePage;
